import React, { useState, useEffect } from 'react';
import SidebarPanel from './SidebarPanel';

const AgentFormPanel = ({ agent, isOpen, onClose, onSave, healthSystems = [], organizations = [] }) => {
  const [formData, setFormData] = useState({
    agent_id: '',
    agent_name: '',
    phone_number: '',
    description: '',
    health_system: '',
    sales_organization: '',
    retell_config: '{}',
    is_active: true,
  });
  const [assignmentType, setAssignmentType] = useState('none'); // 'none', 'healthcare', 'organization'
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (agent) {
      const hasHealthSystem = agent.health_system;
      const hasOrganization = agent.sales_organization;
      setFormData({
        agent_id: agent.agent_id || '',
        agent_name: agent.agent_name || '',
        phone_number: agent.phone_number || '',
        description: agent.description || '',
        health_system: agent.health_system || '',
        sales_organization: agent.sales_organization || '',
        retell_config: JSON.stringify(agent.retell_config || {}, null, 2),
        is_active: agent.is_active !== undefined ? agent.is_active : true,
      });
      setAssignmentType(hasHealthSystem ? 'healthcare' : hasOrganization ? 'organization' : 'none');
    } else {
      setFormData({
        agent_id: '',
        agent_name: '',
        phone_number: '',
        description: '',
        health_system: '',
        sales_organization: '',
        retell_config: '{}',
        is_active: true,
      });
      setAssignmentType('healthcare');
    }
    setErrors({});
  }, [agent, isOpen]);

  const handleAssignmentTypeChange = (type) => {
    setAssignmentType(type);
    if (type === 'healthcare') {
      setFormData(prev => ({ ...prev, sales_organization: '' }));
    } else if (type === 'organization') {
      setFormData(prev => ({ ...prev, health_system: '' }));
    } else {
      setFormData(prev => ({ ...prev, health_system: '', sales_organization: '' }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.agent_id.trim()) newErrors.agent_id = 'Agent ID is required';
    if (!formData.agent_name.trim()) newErrors.agent_name = 'Agent name is required';
    if (formData.retell_config.trim()) {
      try {
        JSON.parse(formData.retell_config);
      } catch (e) {
        newErrors.retell_config = 'Invalid JSON format';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        ...formData,
        retell_config: formData.retell_config.trim() ? JSON.parse(formData.retell_config) : {},
        health_system: formData.health_system || null,
        sales_organization: formData.sales_organization || null,
      };
      await onSave(payload);
      onClose();
    } catch (error) {
      setErrors({ general: error.message || 'Failed to save agent' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SidebarPanel
      isOpen={isOpen}
      onClose={onClose}
      title={agent ? 'Edit AI Agent' : 'Add AI Agent'}
    >
      <div className="p-3">
        <form onSubmit={handleSubmit}>
          {errors.general && (
            <div className="alert alert-danger">{errors.general}</div>
          )}

          <div className="mb-3">
            <label className="form-label">Agent ID <span className="text-danger">*</span></label>
            <input
              type="text"
              className={`form-control ${errors.agent_id ? 'is-invalid' : ''}`}
              name="agent_id"
              value={formData.agent_id}
              onChange={handleChange}
              placeholder="agent_xxxxx"
              disabled={!!agent}
            />
            {errors.agent_id && <div className="invalid-feedback">{errors.agent_id}</div>}
            <div className="form-text">Retell AI agent identifier</div>
          </div>

          <div className="mb-3">
            <label className="form-label">Agent Name <span className="text-danger">*</span></label>
            <input
              type="text"
              className={`form-control ${errors.agent_name ? 'is-invalid' : ''}`}
              name="agent_name"
              value={formData.agent_name}
              onChange={handleChange}
              placeholder="Customer Support Agent"
            />
            {errors.agent_name && <div className="invalid-feedback">{errors.agent_name}</div>}
          </div>

          <div className="mb-3">
            <label className="form-label">Phone Number</label>
            <input
              type="text"
              className="form-control"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleChange}
              placeholder="+1234567890"
            />
          </div>

          <div className="mb-3">
            <label className="form-label">Description</label>
            <textarea
              className="form-control"
              rows="3"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Agent description..."
            />
          </div>

          <div className="mb-3">
            <label className="form-label d-block">Assignment Type</label>
            <div className="btn-group" role="group">
              <input
                type="radio"
                className="btn-check"
                name="assignmentType"
                id="assignmentHealthcare"
                checked={assignmentType === 'healthcare'}
                onChange={() => handleAssignmentTypeChange('healthcare')}
              />
              <label className="btn btn-outline-primary" htmlFor="assignmentHealthcare">Health System</label>

              <input
                type="radio"
                className="btn-check"
                name="assignmentType"
                id="assignmentOrganization"
                checked={assignmentType === 'organization'}
                onChange={() => handleAssignmentTypeChange('organization')}
              />
              <label className="btn btn-outline-info" htmlFor="assignmentOrganization">Organization</label>

              <input
                type="radio"
                className="btn-check"
                name="assignmentType"
                id="assignmentNone"
                checked={assignmentType === 'none'}
                onChange={() => handleAssignmentTypeChange('none')}
              />
              <label className="btn btn-outline-secondary" htmlFor="assignmentNone">None</label>

            </div>
          </div>

          {assignmentType === 'healthcare' && (
            <div className="mb-3">
              <label className="form-label">Health System</label>
              <select
                className="form-select"
                name="health_system"
                value={formData.health_system}
                onChange={handleChange}
              >
                <option value="">Select a health system</option>
                {Array.isArray(healthSystems) && healthSystems.map(hs => (
                  <option key={hs.id} value={hs.id}>{hs.name}</option>
                ))}
              </select>
            </div>
          )}

          {assignmentType === 'organization' && (
            <div className="mb-3">
              <label className="form-label">Organization</label>
              <select
                className="form-select"
                name="sales_organization"
                value={formData.sales_organization}
                onChange={handleChange}
              >
                <option value="">Select an organization</option>
                {Array.isArray(organizations) && organizations.map(org => (
                  <option key={org.id} value={org.id}>{org.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="mb-3">
            <label className="form-label">Retell Config (JSON)</label>
            <textarea
              className={`form-control ${errors.retell_config ? 'is-invalid' : ''}`}
              rows="8"
              name="retell_config"
              value={formData.retell_config}
              onChange={handleChange}
              placeholder='{"key": "value"}'
              style={{ fontFamily: 'monospace', fontSize: '0.9em' }}
            />
            {errors.retell_config && <div className="invalid-feedback">{errors.retell_config}</div>}
          </div>

          <div className="mb-3">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                name="is_active"
                id="is_active"
                checked={formData.is_active}
                onChange={handleChange}
              />
              <label className="form-check-label" htmlFor="is_active">Active</label>
            </div>
          </div>

          <div className="d-flex gap-2">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Agent'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </SidebarPanel>
  );
};

export default AgentFormPanel;
